import React, { useState } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material'
import {
  Category as CategoryIcon,
  Collections as CollectionsIcon,
  Book as BookIcon,
} from '@mui/icons-material'
import CategoriesMUI from './CategoriesMUI'
import CollectionsEnhancedMUI from './CollectionsEnhancedMUI'
import CataloguesMUI from './CataloguesMUI'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`catalogue-tabpanel-${index}`}
      aria-labelledby={`catalogue-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `catalogue-tab-${index}`,
    'aria-controls': `catalogue-tabpanel-${index}`,
  }
}

interface CatalogueManagementTabsProps {
  defaultTab?: 'categories' | 'collections' | 'catalogues'
}

const CatalogueManagementTabs: React.FC<CatalogueManagementTabsProps> = ({ 
  defaultTab = 'categories' 
}) => {
  const tabIndexMap = {
    categories: 0,
    collections: 1,
    catalogues: 2,
  }

  const [activeTab, setActiveTab] = useState(tabIndexMap[defaultTab])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [pendingTab, setPendingTab] = useState<number | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (hasUnsavedChanges) {
      setPendingTab(newValue)
      setShowConfirmDialog(true)
    } else {
      setActiveTab(newValue)
    }
  }

  const handleConfirmTabChange = () => {
    if (pendingTab !== null) {
      setActiveTab(pendingTab)
      setHasUnsavedChanges(false)
      setPendingTab(null)
    }
    setShowConfirmDialog(false)
  }

  const handleCancelTabChange = () => {
    setPendingTab(null)
    setShowConfirmDialog(false)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="catalogue management tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '1rem',
              minHeight: 64,
            },
            '& .Mui-selected': {
              fontWeight: 600,
            },
          }}
        >
          <Tab
            icon={<CategoryIcon />}
            iconPosition="start"
            label="Categories"
            {...a11yProps(0)}
          />
          <Tab
            icon={<CollectionsIcon />}
            iconPosition="start"
            label="Collections"
            {...a11yProps(1)}
          />
          <Tab
            icon={<BookIcon />}
            iconPosition="start"
            label="Catalogues"
            {...a11yProps(2)}
          />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <CategoriesMUI />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <CollectionsEnhancedMUI />
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        <CataloguesMUI />
      </TabPanel>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelTabChange}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to switch tabs? Your changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTabChange} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmTabChange} color="error" variant="contained">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CatalogueManagementTabs
